import React from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { Button, message } from 'antd';
import { useTranslation } from 'react-i18next';

interface ExcelExportButtonProps {
  // Accepts rows directly, or a lazy provider so callers can defer building
  // export rows until the user actually clicks the button.
  data: any[] | (() => any[]);
  filename?: string;
  sheetName?: string;
  columns?: { key: string; title?: string }[]; // ordered visible columns
  size?: 'small' | 'middle' | 'large';
}

const ExcelExportButton = ({ data, filename = "export.xlsx", sheetName = "Sheet1", columns, size = 'middle' }: ExcelExportButtonProps) => {
  const { t } = useTranslation();

  const handleExport = () => {
    const rows = typeof data === 'function' ? data() : data;
    if (!rows || rows.length === 0) {
      message.warning(t('common.noExportData'));
      return;
    }

    let exportData = rows;
    if (Array.isArray(columns) && columns.length > 0) {
      // map each row to ordered keys; include titles as header row via header mapping
      exportData = rows.map((row) => {
        const obj: any = {};
        columns.forEach((col) => {
          obj[col.title || col.key] = row[col.key];
        });
        return obj;
      });
    }

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, filename);
  };

  return (
    <Button type="primary" size={size} onClick={handleExport}>
      {t('common.exportExcel')}
    </Button>
  );
};

export default ExcelExportButton;
